import { matchPath, useOutletContext } from "react-router-dom";
import { can } from "@/auth/capabilities";
import type { PlatformAdmin } from "@/types/api";

// The platform (super-admin) capability axis, mirrored from internal/tenancy/
// policy.go. Deliberately SEPARATE from the tenant capability union in
// auth/capabilities: a platform admin is a different kind of principal, not a
// tenant role with more permissions (6.6), and merging the two unions would let a
// tenant screen be gated on `platform:*` by autocomplete alone.
export type PlatformCapability =
  | "platform:tenant.onboard"
  | "platform:tenant.suspend"
  | "platform:tenant.read"
  | "platform:tenant.delete"
  | "platform:impersonate"
  | "platform:tenant.setup"
  | "platform:tenant.reset"
  | "platform:admin.manage"
  | "platform:channel.manage"
  | "platform:feature.manage"
  | "platform:demo.manage"
  | "platform:lead.manage"
  | "platform:plan.manage"
  | "platform:catalog.manage"
  | "platform:ops.manage"
  | "platform:staff.recover";

/** What a console screen or control needs; an array means any-of. */
export type PlatformNeed = PlatformCapability | readonly PlatformCapability[];

// The capability each console route needs, as one table read by both the route
// guard (PlatformCapabilityRoute) and the console sidebar - the same arrangement
// as the tenant side's auth/routeAccess, for the same reason: a destination that
// is hidden from the nav but reachable by URL is not gated, it is just untidy.
//
// Each entry matches the capability the screen's FIRST request is gated on
// server-side, so the console and the 403 agree:
//   Packaging and Catalog are `platform:tenant.read` because that is what their
//   reads need - a support admin sees them, and the write actions inside 403 on
//   platform:plan.manage / platform:catalog.manage. Gating the screens on the
//   write capability would hide information support is meant to have.
//
// "/platform" itself is deliberately absent: it is where the guard redirects, so
// gating it would make an admin who fails the check bounce forever. Its figures
// come from platform:tenant.read, which every platform role holds today.
const ROUTE_CAPABILITY: Record<string, PlatformNeed> = {
  "/platform/tenants": "platform:tenant.read",
  // Literal path before the pattern below: onboarding is its own capability, and
  // "/platform/tenants/onboard" would otherwise read as a tenant id.
  "/platform/tenants/onboard": "platform:tenant.onboard",
  "/platform/tenants/:id": "platform:tenant.read",
  "/platform/demo": "platform:demo.manage",
  // Personal data (contact details, consent): its own capability, not demo.manage.
  "/platform/leads": "platform:lead.manage",
  "/platform/packaging": "platform:tenant.read",
  "/platform/catalog": "platform:tenant.read",
  "/platform/sms": "platform:tenant.read",
  // Platform machinery, not shop data: its own capability, so support (which
  // holds platform:tenant.read) neither sees stuck events nor can retry one.
  "/platform/ops/stuck-events": "platform:ops.manage",
  "/platform/admins": "platform:admin.manage",
  // The console's own global settings (default SMS allowance, the print footer
  // stamped on every tenant's documents) are gated on platform:channel.manage.
  "/platform/settings": "platform:channel.manage",
};

// Patterns ranked by literal-segment count so a concrete path wins over one with
// a param: matchPath does not rank, and the first match is taken.
const PATTERNS = Object.keys(ROUTE_CAPABILITY).sort(
  (a, b) =>
    b.split("/").filter((s) => !s.startsWith(":")).length -
    a.split("/").filter((s) => !s.startsWith(":")).length,
);

/**
 * The capability a console path needs, or undefined when any signed-in platform
 * admin may open it (the dashboard).
 */
export function platformCapabilityForPath(pathname: string): PlatformNeed | undefined {
  const direct = ROUTE_CAPABILITY[pathname];
  if (direct) return direct;
  const hit = PATTERNS.find((p) => matchPath({ path: p, end: true }, pathname));
  return hit ? ROUTE_CAPABILITY[hit] : undefined;
}

/**
 * The console's counterpart to the tenant app's useAuth().can, for gating a
 * CONTROL rather than a route: the onboard button, the suspend button, the danger
 * zone. Reads the admin from outlet context (set by PlatformProtectedRoute), so it
 * works on any page rendered inside the console shell. Fails closed.
 */
export function usePlatformCan(): (need: PlatformNeed) => boolean {
  const admin = useOutletContext<PlatformAdmin | null>();
  return (need) => can(admin?.capabilities, need);
}
